import type { FormulaError, ProductLine } from '../types/product';

type TokenType = 'number' | 'ident' | 'op' | 'lparen' | 'rparen' | 'comma' | 'end';

interface Token {
  type: TokenType;
  value: string;
  pos: number;
}

const FUNCTIONS = new Set(['SUM', 'MIN', 'MAX', 'ROUND', 'CEIL', 'FLOOR', 'ABS']);

export class FormulaSyntaxError extends Error {}

export function isFormula(value: number | string | undefined | null): value is string {
  return typeof value === 'string' && value.trim().startsWith('=');
}

/** Haalt het gedeelte na de '=' op, zodat de parser alleen de expressie ziet. */
function formulaBody(value: string): string {
  const trimmed = value.trim();
  return trimmed.startsWith('=') ? trimmed.slice(1) : trimmed;
}

function tokenize(input: string): Token[] {
  const tokens: Token[] = [];
  /**
   * Onthoudt per open haakje of het bij een functie hoort. Binnen een
   * functieaanroep is een komma altijd een argumentscheider (MAX(1,2) = 2);
   * daarbuiten is een komma tussen cijfers een decimaalteken (9,5). Zonder dit
   * onderscheid leest MAX(1,2) als het getal 1,2.
   */
  const parenKinds: Array<'func' | 'group'> = [];
  const insideFunctionArgs = () => parenKinds[parenKinds.length - 1] === 'func';
  let i = 0;

  while (i < input.length) {
    const ch = input[i];

    if (/\s/.test(ch)) {
      i += 1;
      continue;
    }

    if (/[0-9]/.test(ch) || (ch === '.' && /[0-9]/.test(input[i + 1] ?? ''))) {
      let j = i;
      while (j < input.length && /[0-9._,]/.test(input[j])) {
        if (input[j] === ',') {
          // Alleen decimaalteken buiten een argumentenlijst, en alleen met een
          // cijfer erachter.
          if (insideFunctionArgs() || !/[0-9]/.test(input[j + 1] ?? '')) break;
        }
        j += 1;
      }
      const raw = input.slice(i, j).replace(/_/g, '').replace(',', '.');
      tokens.push({ type: 'number', value: raw, pos: i });
      i = j;
      continue;
    }

    if (/[A-Za-z_]/.test(ch)) {
      let j = i;
      while (j < input.length && /[A-Za-z0-9_]/.test(input[j])) j += 1;
      tokens.push({ type: 'ident', value: input.slice(i, j), pos: i });
      i = j;
      continue;
    }

    if ('+-*/%'.includes(ch)) {
      tokens.push({ type: 'op', value: ch, pos: i });
      i += 1;
      continue;
    }
    if (ch === '(') {
      const previous = tokens[tokens.length - 1];
      const isFunctionCall =
        previous?.type === 'ident' && FUNCTIONS.has(previous.value.toUpperCase());
      parenKinds.push(isFunctionCall ? 'func' : 'group');
      tokens.push({ type: 'lparen', value: ch, pos: i });
      i += 1;
      continue;
    }
    if (ch === ')') {
      parenKinds.pop();
      tokens.push({ type: 'rparen', value: ch, pos: i });
      i += 1;
      continue;
    }
    if (ch === ',' || ch === ';') {
      tokens.push({ type: 'comma', value: ',', pos: i });
      i += 1;
      continue;
    }

    throw new FormulaSyntaxError(`Onbekend teken "${ch}" op positie ${i + 1}`);
  }

  tokens.push({ type: 'end', value: '', pos: input.length });
  return tokens;
}

interface ParseContext {
  /** Levert de waarde van een andere regel; gooit bij onbekend of kringverwijzing. */
  resolve: (code: string) => number;
}

/**
 * Recursive-descent parser + evaluator in één pass. De invoer is klein (één
 * regel) en wordt bij elke herberekening opnieuw gelezen, dus een AST-cache
 * levert hier niets op.
 */
function parseExpression(tokens: Token[], ctx: ParseContext): number {
  let pos = 0;

  const peek = () => tokens[pos];
  const next = () => tokens[pos++];

  function expect(type: TokenType, what: string): Token {
    const token = peek();
    if (token.type !== type) {
      throw new FormulaSyntaxError(`${what} verwacht op positie ${token.pos + 1}`);
    }
    return next();
  }

  function parseArguments(): number[] {
    const args: number[] = [];
    expect('lparen', 'Haakje openen');
    if (peek().type === 'rparen') {
      next();
      return args;
    }
    for (;;) {
      args.push(parseSum());
      const token = peek();
      if (token.type === 'comma') {
        next();
        continue;
      }
      expect('rparen', 'Haakje sluiten');
      return args;
    }
  }

  function applyFunction(name: string, args: number[]): number {
    switch (name) {
      case 'SUM':
        return args.reduce((total, value) => total + value, 0);
      case 'MIN':
        if (!args.length) throw new FormulaSyntaxError('MIN heeft minstens één waarde nodig');
        return Math.min(...args);
      case 'MAX':
        if (!args.length) throw new FormulaSyntaxError('MAX heeft minstens één waarde nodig');
        return Math.max(...args);
      case 'ABS':
        if (args.length !== 1) throw new FormulaSyntaxError('ABS verwacht één waarde');
        return Math.abs(args[0]);
      case 'CEIL':
        if (args.length !== 1) throw new FormulaSyntaxError('CEIL verwacht één waarde');
        return Math.ceil(args[0]);
      case 'FLOOR':
        if (args.length !== 1) throw new FormulaSyntaxError('FLOOR verwacht één waarde');
        return Math.floor(args[0]);
      case 'ROUND': {
        if (!args.length || args.length > 2) {
          throw new FormulaSyntaxError('ROUND verwacht ROUND(waarde) of ROUND(waarde, decimalen)');
        }
        const factor = 10 ** (args[1] ?? 0);
        return Math.round(args[0] * factor) / factor;
      }
      default:
        throw new FormulaSyntaxError(`Onbekende functie ${name}`);
    }
  }

  function parsePrimary(): number {
    const token = peek();

    if (token.type === 'number') {
      next();
      const value = Number(token.value);
      if (Number.isNaN(value)) {
        throw new FormulaSyntaxError(`Ongeldig getal "${token.value}"`);
      }
      // Achtergeplaatst percentage: 10% -> 0,1
      if (peek().type === 'op' && peek().value === '%') {
        next();
        return value / 100;
      }
      return value;
    }

    if (token.type === 'ident') {
      next();
      const upper = token.value.toUpperCase();
      if (FUNCTIONS.has(upper)) {
        return applyFunction(upper, parseArguments());
      }
      if (peek().type === 'lparen') {
        throw new FormulaSyntaxError(`Onbekende functie ${token.value}`);
      }
      return ctx.resolve(token.value);
    }

    if (token.type === 'lparen') {
      next();
      const value = parseSum();
      expect('rparen', 'Haakje sluiten');
      return value;
    }

    throw new FormulaSyntaxError(`Onverwacht einde of teken op positie ${token.pos + 1}`);
  }

  function parseUnary(): number {
    const token = peek();
    if (token.type === 'op' && (token.value === '-' || token.value === '+')) {
      next();
      const value = parseUnary();
      return token.value === '-' ? -value : value;
    }
    return parsePrimary();
  }

  function parseProduct(): number {
    let left = parseUnary();
    for (;;) {
      const token = peek();
      if (token.type !== 'op' || (token.value !== '*' && token.value !== '/')) return left;
      next();
      const right = parseUnary();
      if (token.value === '/') {
        if (right === 0) throw new FormulaSyntaxError('Deling door nul');
        left /= right;
      } else {
        left *= right;
      }
    }
  }

  function parseSum(): number {
    let left = parseProduct();
    for (;;) {
      const token = peek();
      if (token.type !== 'op' || (token.value !== '+' && token.value !== '-')) return left;
      next();
      const right = parseProduct();
      left = token.value === '+' ? left + right : left - right;
    }
  }

  const result = parseSum();
  if (peek().type !== 'end') {
    throw new FormulaSyntaxError(`Onverwacht teken op positie ${peek().pos + 1}`);
  }
  return result;
}

/**
 * Rekent de uren van alle regels van één variant uit, inclusief formules.
 * Kringverwijzingen worden gedetecteerd via de resolutieketen en leveren een
 * fout op de betrokken regel op, niet een oneindige lus.
 */
export function evaluateVariantHours(
  lines: ProductLine[],
  variantId: string,
): { hoursByCode: Record<string, number>; errors: FormulaError[] } {
  const byCode = new Map<string, ProductLine>();
  const errors: FormulaError[] = [];

  for (const line of lines) {
    if (byCode.has(line.code)) {
      errors.push({
        code: line.code,
        variantId,
        message: `Regelcode "${line.code}" komt meer dan één keer voor`,
      });
      continue;
    }
    byCode.set(line.code, line);
  }

  const resolved = new Map<string, number>();
  const inProgress = new Set<string>();
  const failed = new Set<string>();

  function valueFor(code: string, chain: string[]): number {
    if (resolved.has(code)) return resolved.get(code) as number;
    if (failed.has(code)) throw new FormulaSyntaxError(`Regel "${code}" kon niet berekend worden`);

    const line = byCode.get(code);
    if (!line) throw new FormulaSyntaxError(`Onbekende verwijzing "${code}"`);

    if (inProgress.has(code)) {
      throw new FormulaSyntaxError(
        `Kringverwijzing: ${[...chain, code].join(' → ')}`,
      );
    }

    const raw = line.hours[variantId];
    if (raw === undefined || raw === null || raw === '') {
      resolved.set(code, 0);
      return 0;
    }

    if (!isFormula(raw)) {
      const numeric = typeof raw === 'number' ? raw : Number(String(raw).replace(',', '.'));
      if (!Number.isFinite(numeric)) {
        throw new FormulaSyntaxError(`"${raw}" is geen getal`);
      }
      resolved.set(code, numeric);
      return numeric;
    }

    inProgress.add(code);
    try {
      const tokens = tokenize(formulaBody(raw));
      const value = parseExpression(tokens, {
        resolve: (ref) => valueFor(ref, [...chain, code]),
      });
      if (!Number.isFinite(value)) {
        throw new FormulaSyntaxError('Uitkomst is geen geldig getal');
      }
      resolved.set(code, value);
      return value;
    } finally {
      inProgress.delete(code);
    }
  }

  for (const line of byCode.values()) {
    try {
      valueFor(line.code, []);
    } catch (error) {
      failed.add(line.code);
      resolved.set(line.code, 0);
      errors.push({
        code: line.code,
        variantId,
        message: error instanceof Error ? error.message : 'Onbekende fout',
      });
    }
  }

  const hoursByCode: Record<string, number> = {};
  for (const [code, value] of resolved.entries()) hoursByCode[code] = value;

  return { hoursByCode, errors };
}

/**
 * Controleert een losse formule zonder hem uit te rekenen: syntaxis en of alle
 * verwijzingen bestaan. Gebruikt bij het opslaan van een concept, zodat een
 * kapotte formule nooit gepubliceerd kan worden.
 */
export function validateFormula(
  expression: string,
  availableCodes: string[],
): { valid: boolean; message?: string } {
  if (!isFormula(expression)) return { valid: true };
  const known = new Set(availableCodes);
  try {
    const tokens = tokenize(formulaBody(expression));
    parseExpression(tokens, {
      resolve: (code) => {
        if (!known.has(code)) {
          throw new FormulaSyntaxError(`Onbekende verwijzing "${code}"`);
        }
        return 1;
      },
    });
    return { valid: true };
  } catch (error) {
    return { valid: false, message: error instanceof Error ? error.message : 'Ongeldige formule' };
  }
}

